import { useState, useEffect } from "react";
import { Modal, Box, Typography, Stack, IconButton, Button, Link } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppointmentForm from "./components/appointmentForm";
import AppointmentSummary from "./components/appointmentSummary";
import DateSlot from "./components/dateSlot";
import { ArrowCircle } from "./components/arrowUp";
import StoreAppointmentSummary from "./components/summary/storeAppointmentSummary";
import { BOOK_AN_APPOINTMENT_UNIVERS } from "../../../constants/common";
import AsyncCall from "../../../_foundation/gtm/async.service";
import { CLICK, RDV_TUNNEL_EVENTS } from "../../../_foundation/constants/gtm";
import { useLocation } from "react-router-dom";

const styles = {
  modalBox: {
    background: "#fff",
    width: "100%",
    mt: 0,
    maxWidth: "774px",
    borderRadius: "16px",
    position: "relative",
  },
  header: {
    background: "#5C87A0",
    padding: {
      xs: "24px 12px",
      sm: "24px 12px",
      md: "32px 24px",
      lg: "32px 24px",
    },
    color: "#fff",
    textAlign: "center",
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
    position: "relative",
  },
  optionCard: (active: boolean) => ({
    background: "#fff",
    borderRadius: {
      xs: "0",
      md: "16px",
    },
    borderBottom: {
      xs: "1px solid #E0E0E0",
      md: "none",
    },
    "&:last-child": {
      borderBottom: {
        xs: "none",
      },
      // paddingBottom: {
      //   xs: 0,
      //   sm: 0,
      // },
    },
    padding: {
      xs: "8px 0 12px 0",
      sm: "8px 0 12px 0",
      md: "24px 16px",
      lg: "24px 16px",
    },
    marginBottom: "12px",
    boxShadow: {
      xs: "none",
      md: active ? "0 0 0 2px #3c80e0 inset, 0 2px 8px rgba(0,0,0,0.15)" : "0 4px 16px 0 rgba(0, 0, 0, 0.08);",
    },
    cursor: "pointer",
    transition: "0.25s",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  }),
  optionCardInline: {
    background: "#fff",
    borderRadius: "16px",
    padding: "48px",
    alignItems: "center",
  },
  btn: {
    fontSize: "14px",
    fontWeight: 700,
    background: "#C41E3B",
    color: "#fff",
    padding: "14px 16px",
    borderRadius: "8px",
    margin: "auto",
    marginTop: "24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "border-radius 0.3s ease-in-out, background-color 0.3s ease-in-out",

    "&:hover": {
      background: "#A81832",
      borderRadius: "30px",
    },
  },
};

const appointmentOptions = [
  {
    key: "commercial",
    label: "En magasin",
    subtitle: "Rencontrez nos experts dans le magasin le plus proche.",
    tag: "Gratuit",
    gtmKey: "en_magasin",
  },
  {
    key: "phone",
    label: "Par téléphone ou en visio",
    subtitle: "Échangez avec un expert par téléphone ou en visio, depuis chez vous",
    tag: "Gratuit",
    gtmKey: "par_telephone_ou_en_visio",
  },
  {
    key: "CAD",
    label: "À domicile",
    subtitle: "Un conseiller se déplace chez vous pour étudier votre projet.",
    tag: "89€, déduits de votre commande",
    gtmKey: "a_domicile",
  },
];

interface AppointMentModalProps {
  open?: boolean;
  onClose?: () => void;
  initialOption?: string;
  postalCode?: string;
  openedFrom: "header" | "homepage" | "RDV Page" | "produit page" | "SEO page";
}

const AppointmentModal: React.FC<AppointMentModalProps> = ({
  open,
  onClose,
  initialOption,
  openedFrom = "",
  postalCode = "",
}) => {
  const { pathname } = useLocation();

  const [selectedOption, setSelectedOption] = useState<string>(initialOption || "");
  const [currGtmKey, setCurrGTMKey] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [dateSlot, setDateSlot] = useState(false);
  const [savedFormData, setSavedFormData] = useState(null);
  const [appointmentData, setAppointmentData] = useState<any>(null);
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [confirmationType, setConfirmationType] = useState<"APPOINTMENT" | "CALLBACK" | null>(null);
  const [currentCommunicationType, setCurrentCommunicationType] = useState<string | undefined>(undefined);

  const isInlineMode = open === undefined;
  const modalOpen = isInlineMode ? internalOpen : !!open;

  const currentModalScreen = open
    ? currGtmKey
      ? dateSlot || appointmentData
        ? confirmed
          ? confirmationType === "APPOINTMENT"
            ? "appointment_submitted_screen"
            : "callback_submitted_screen"
          : "calendar_screen"
        : "form_data_screen"
      : "menu_selection_screen"
    : "modal_closed";

  // console.log("currentModalScreen: ", currentModalScreen);

  useEffect(() => {
    if (initialOption) {
      setSelectedOption(initialOption);
      const subMenuData = appointmentOptions?.find((option) => option?.key === selectedOption);
      setCurrGTMKey(subMenuData?.gtmKey || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOption]);

  /* RDV GTM EVENT 1: GTM EVENT FIRE ON FORM MOUNT */
  useEffect(() => {
    if (open || internalOpen) {
      AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_VIEW, { navigation_location: openedFrom });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, internalOpen]);

  /* RDV GTM EVENT 2: selection of type of rdv form */
  useEffect(() => {
    if (currGtmKey) {
      AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_TYPE_SELECT, {
        navigation_location: openedFrom,
        rdv_type: currGtmKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currGtmKey]);

  const resetModal = () => {
    setSelectedOption("");
    setCurrGTMKey(null);
    setConfirmed(false);
    setDateSlot(false);
    setConfirmationType(null);

    setFormData(null);
    setSavedFormData(null);
    setAppointmentData(null);
    setSelectedStore(null);

    if (isInlineMode) {
      setInternalOpen(false);
    } else {
      onClose?.();
    }
  };

  const handleBack = () => {
    if (dateSlot) {
      setDateSlot(false);
    } else if (selectedOption && !confirmed) {
      setSelectedOption("");
      setCurrGTMKey(null);
    }
  };

  const isVisio = selectedOption === "phone" && appointmentData?.type === "visio";

  const getContent = () => {
    if (confirmed && confirmationType === "CALLBACK") {
      return {
        title: "Demande de rappel enregistrée !",
        desc: "Un conseiller Lapeyre va vous contacter très prochainement",
      };
    }

    if (confirmed) {
      const messages: Record<string, string> = {
        commercial: "Rendez-vous confirmé !",
        phone: isVisio ? "Rendez-vous en visio confirmé !" : "Demande de rappel enregistrée !",
        CAD: "Rendez-vous à domicile confirmé !",
      };
      const description: Record<string, string> = {
        commercial: "Nous avons hâte de vous rencontrer et de vous accompagner dans votre projet",
        phone: "Un conseiller Lapeyre va vous contacter très prochainement",
        CAD: "Notre expert viendra directement chez vous pour étudier votre projet",
      };
      return { title: messages[selectedOption] || "", desc: description[selectedOption] };
    }

    const titles: Record<string, { title: string; desc: string }> = {
      commercial: {
        title: "Rendez-vous en magasin",
        desc: "Rencontrez nos experts dans le magasin le plus proche de chez vous",
      },
      phone: {
        title:
          dateSlot && currentCommunicationType === "visio"
            ? "Rendez-vous en visio"
            : dateSlot
            ? "Par téléphone"
            : "Par téléphone ou en visio",
        desc:
          dateSlot && currentCommunicationType === "visio"
            ? "Échangez avec un expert en visio"
            : dateSlot
            ? "Échangez avec un expert par téléphone"
            : "Échangez avec un expert par téléphone ou en visio",
      },
      CAD: { title: "Rendez-vous à domicile", desc: "Un conseiller se déplace chez vous pour étudier votre projet" },
    };

    return (
      titles[selectedOption] || {
        title: "Prenez rendez-vous\navec nos experts",
        desc: "En magasin, à domicile ou par téléphone : nos conseillers vous accompagnent.",
      }
    );
  };

  const mapUniversValuesToTitles = (univers?: string[]) => {
    if (!univers || univers.length === 0) return undefined;

    return univers
      .map((value) => {
        const found = BOOK_AN_APPOINTMENT_UNIVERS.find((u) => u.value === value);
        return found?.title || value;
      })
      .join(", ");
  };

  const onRdvSubMenuTypeSelect = (option: Record<string, any>) => {
    setSelectedOption(option.key);
    setCurrGTMKey(option?.gtmKey || null);
  };

  const onFormSubmit = (formData: any, store: any, type?: "APPOINTMENT" | "CALLBACK") => {
    /* RDV GTM EVENT 3: rdv form info data for business */
    AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_INFO, {
      navigation_location: openedFrom,
      project_universe: formData?.Univers?.[0],
      address_postal_code: formData?.codePostal,
      store_id: formData?.codeMag,
      store_name: store?.label,
    });

    if (type === "CALLBACK") {
      setAppointmentData(formData);
      setConfirmed(true);
      setConfirmationType("CALLBACK");
      return;
    }
    // console.log("CHILD FORM DATA:", formData);
    setFormData(formData);
    setSavedFormData(formData as any); // store the form data
    setDateSlot(true); // move to date/slot step
  };

  const onDateSlotSubmit = (payload) => {
    // console.log("payload: ", payload);

    /* RDV GTM EVENT 5: RDV form submit */
    const subMenuData = appointmentOptions?.find((option) => option?.key === selectedOption);
    const eventDataLayerObj = {
      step_name: payload.confirmationType === "CALLBACK" ? "callback_confirmation" : "confirmation",
      navigation_location: openedFrom,
      conversion_type: payload.confirmationType === "CALLBACK" ? "callback_request" : "appointment_booking",
      rdv_type_final: subMenuData?.gtmKey,
      project_universe: formData?.Univers?.[0],
      store_id: payload?.formData?.codeMag,
      address_postal_code: payload?.formData?.codePostal,
      // config_id: "{{lead_ref}}", // TODO: FIND WHAT TO PASS HERE
    };
    if (payload.confirmationType !== "CALLBACK") {
      eventDataLayerObj["rdv_date"] = payload?.date;
    }

    AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_OUTCOME_COMPLETE, eventDataLayerObj);
    AsyncCall.sendClick(CLICK.FORM_SUBMIT, {
      btn_label: "Choisir mon créneau",
      form_id: "rdv_store_form",
      form_name: "rdvStoreForm",
      form_destination: pathname.replace("/", ""),
      email: formData.email,
      phone_number: formData.telephone,
      address_last_name: formData.nom,
      address_first_name: formData.prenom,
    });

    const finalPayload = {
      ...(payload.formData || {}),
      date: payload.date,
      slot: payload.slot,
      locationId: payload.locationId,
      store: selectedStore,
    };
    setDateSlot(false);
    setAppointmentData(finalPayload);
    setConfirmed(true);
    setConfirmationType(payload.confirmationType || "APPOINTMENT");
    // console.log("Final Payload:", finalPayload);
  };

  const onModalClose = () => {
    /* RDV GTM EVENT 6: RDV form exit */
    AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_EXIT, {
      navigation_location: openedFrom,
      action: "close_popin",
      abandoned_step: currentModalScreen,
    });
    resetModal();
  };

  const { title, desc } = getContent();

  return (
    <>
      {isInlineMode && (
        <Stack sx={styles.optionCardInline}>
          <Box>
            <Box
              sx={{
                display: "flex",
                padding: "0 8px",
                justifyContent: "center",
                alignItems: "center",
                width: "fit-content",
                borderRadius: "100px",
                backgroundColor: "#EFF3F6",
                marginBottom: "12px",
              }}>
              {/* <Typography fontSize={12} fontWeight={600} color="#5D89A2" mb={0}>
                Gratuit & Sans engagement
              </Typography> */}
            </Box>
            <Typography variant="h2" fontSize={26} fontWeight={700} lineHeight="32px" color="#212121" mb={1}>
              Prenez rendez-vous avec nos experts
            </Typography>
            <Typography fontSize={16} fontWeight={400} lineHeight="24px" color="#000">
              En magasin, à domicile ou en visio : nos conseillers vous accompagnent dans votre projet
            </Typography>

            <Box sx={{ borderBottom: "2px solid #F0F0F0", mt: "18px", mb: 3 }}></Box>
          </Box>

          {appointmentOptions.map((option) => (
            <Link
              key={option.key}
              sx={{
                ...styles.optionCard(selectedOption === option.key),
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "none",
                },
              }}
              onClick={() => {
                setSelectedOption(option.key);
                setCurrGTMKey(option?.gtmKey || null);
                setInternalOpen(true);
              }}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    padding: "0 8px",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "fit-content",
                    borderRadius: "100px",
                    backgroundColor: "#EFF3F6",
                    marginBottom: "12px",
                  }}>
                  {/* <Typography fontSize={12} fontWeight={600} color="#5D89A2" mb={0}>
                    {option.tag}
                  </Typography> */}
                </Box>
                <Typography fontSize={20} fontWeight={700} lineHeight="24px" color="#212121" mb={1}>
                  {option.label}
                </Typography>
                <Typography fontSize={16} fontWeight={400} lineHeight="24px" color="#000">
                  {option.subtitle}
                </Typography>
              </Box>
              <ArrowCircle size={32} bgColor="#FFF" />
            </Link>
          ))}
        </Stack>
      )}

      <Modal open={modalOpen} onClose={onModalClose}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            px: 2,
          }}>
          <Box sx={styles.modalBox} mx="auto" mt={5}>
            {/* HEADER */}
            <Box sx={styles.header}>
              {selectedOption && !confirmed && (
                <IconButton
                  onClick={handleBack}
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    lineHeight: "20px",
                    position: {
                      xs: "relative",
                      md: "absolute",
                    },
                    top: {
                      xs: 0,
                      md: 16,
                    },
                    left: {
                      xs: 0,
                      md: 16,
                    },
                    padding: {
                      xs: 0,
                      md: 2,
                    },
                    color: "#fff",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}>
                  <ArrowCircle size={32} bgColor="#FFF" direction="left" /> Retour
                </IconButton>
              )}
              <IconButton onClick={onModalClose} sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}>
                <CloseIcon />
              </IconButton>
              {/* {selectedOption && !confirmed && selectedOption !== "CAD" && (
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  bgcolor="#EFF3F6"
                  borderRadius={22}
                  color="#5D89A2"
                  m="auto auto 8px"
                  sx={{
                    maxWidth: "max-content",
                    padding: "2px 12px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  Gratuit
                </Typography>
              )} */}
              <Typography
                variant="h5"
                sx={{
                  textTransform: "initial",
                  fontSize: {
                    xs: "18px",
                    sm: "22px",
                    md: "32px",
                  },
                  fontWeight: 900,
                  fontStyle: "normal",
                  lineHeight: "normal",
                }}>
                {title.split("\n").map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
              </Typography>
              {desc && (
                <Typography
                  fontSize={14}
                  sx={{
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "20px",
                    marginTop: {
                      xs: 2,
                      md: 3,
                    },
                  }}>
                  {desc}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                padding: {
                  xs: "12px",
                  sm: "12px",
                  md: "32px 24px",
                  lg: "32px 24px",
                },
                maxHeight: {
                  xs: "58vh",
                  sm: "58vh",
                  md: "65vh",
                  lg: "65vh",
                },
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#c1c1c1 transparent",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-button": {
                  display: "none",
                  width: 0,
                  height: 0,
                },
              }}>
              {!selectedOption && !confirmed && !dateSlot && (
                <Stack>
                  {appointmentOptions.map((option) => (
                    <Box
                      key={option.key}
                      sx={styles.optionCard(selectedOption === option.key)}
                      onClick={() => onRdvSubMenuTypeSelect(option)}>
                      <Box>
                        {/* <Box
                          sx={{
                            display: "flex",
                            padding: "0 8px",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "fit-content",
                            borderRadius: "100px",
                            backgroundColor: "#EFF3F6",
                            marginBottom: "12px",
                          }}>
                          <Typography fontSize={12} fontWeight={600} color="#5D89A2" mb={0}>
                            {option.tag}
                          </Typography>
                        </Box> */}
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 18,
                              sm: 18,
                              md: 20,
                            },
                          }}
                          fontWeight={700}
                          lineHeight="24px"
                          color="#212121"
                          mb={1}>
                          {option.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 12,
                              sm: 12,
                              md: 16,
                            },
                            lineHeight: {
                              xs: "18px",
                              md: "24px",
                            },
                          }}
                          fontWeight={400}
                          color="#000">
                          {option.subtitle}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          minWidth: {
                            xs: "32px",
                            md: "auto",
                          },
                        }}>
                        <ArrowCircle size={32} bgColor="#FFF" />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}

              {selectedOption && !confirmed && !dateSlot && (
                <AppointmentForm
                  formData={formData}
                  setFormData={setFormData}
                  onClose={() => {
                    setSelectedOption("");
                    setCurrGTMKey(null);
                  }}
                  postalCode={postalCode}
                  onStoreChange={(store) => {
                    setSelectedStore(store);
                  }}
                  onSubmitSuccess={onFormSubmit}
                  selectedOption={selectedOption}
                  onCommunicationTypeChange={setCurrentCommunicationType}
                />
              )}

              {dateSlot && (
                <DateSlot
                  onClose={handleBack}
                  formData={savedFormData}
                  onSubmit={onDateSlotSubmit}
                  openedFrom={openedFrom}
                />
              )}

              {confirmed && confirmationType === "APPOINTMENT" && (
                <Box sx={{ textAlign: "left" }}>
                  {selectedOption === "commercial" && (
                    <StoreAppointmentSummary selectedOption={selectedOption} data={appointmentData} />
                  )}
                  {selectedOption === "phone" && (
                    <StoreAppointmentSummary selectedOption={selectedOption} data={appointmentData} />
                  )}
                  {selectedOption === "CAD" && (
                    <StoreAppointmentSummary selectedOption={selectedOption} data={appointmentData} />
                  )}

                  <Box sx={{ textAlign: "center" }}>
                    <Button sx={styles.btn} onClick={resetModal}>
                      Retour à l’accueil
                    </Button>
                  </Box>
                </Box>
              )}

              {confirmed && confirmationType === "CALLBACK" && (
                <>
                  <AppointmentSummary
                    reminderTitle="Quand serez-vous rappelé(e) ?"
                    reminderDescription="Un conseiller Lapeyre vous contactera rapidement sur le numéro indiqué. Il prendra le temps d'échanger avec vous pour bien comprendre votre projet et vous orienter vers les meilleures solutions."
                    stepsTitle="Comment se déroulera l'appel ?"
                    steps={[
                      "Le conseiller <strong>qualifie votre demande</strong> et comprend vos besoins (budget, délais, contraintes...)",
                      "Il vous <strong>oriente</strong> vers les solutions les plus adaptées à votre projet",
                      "Vous pourrez planifier <strong>un rendez-vous en magasin</strong> ou <strong>à domicile</strong> si nécessaire",
                    ]}
                    messageTitle="Numéro masqué ?"
                    messageDescription="Notre conseiller peut vous appeler depuis un numéro commençant par 01 ou 09. Pensez à décrocher même si le numéro ne vous est pas familier."
                    sections={[
                      {
                        label: "Numéro de téléphone",
                        title: appointmentData?.telephone,
                      },
                      ...(appointmentData?.Univers && appointmentData?.Univers !== "null"
                        ? [
                            {
                              label: "Objet de votre demande",
                              title: mapUniversValuesToTitles(appointmentData?.Univers),
                            },
                          ]
                        : []),
                    ]}
                    sectionsOrder={["details", "reminder", "messages", "steps"]}
                  />

                  <Box sx={{ textAlign: "center" }}>
                    <Button sx={styles.btn} onClick={resetModal}>
                      Retour à l’accueil
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AppointmentModal;
